import _ from "lodash";
import { Form } from "formik";
import moment from "moment/moment";
import React from "react";
import { useHistory } from "react-router-dom";

import { Button2 } from "@/ui2/atoms/Button";
import { Card, CustomForm } from "@/component-library";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { providerRequests } from "@/api/(requests)/provider";
import { testLocator } from "@/lib/helpers/test-locator";

import { CredentialField } from "../components/CredentialField";
import { ProviderSignatureField } from "../components/ProviderSignatureField";

import {
  CAN_BE_ASSISTED_WITH_LIFE_PLANNING_ACTIVITIES_OPTIONS,
  CARE_MANAGEMENT_VALIDATION,
  DO_OTHERS_HELP_WITH_LIFE_ACTIVITIES_OPTIONS,
  HELP_TO_IMPROVE_LIFE_OPTIONS,
} from "./CareManagementForm.schema";
import { CareManagementField } from "./components/CareManagementField";
import { CareManagementRadioField } from "./components/CareManagementRadioField";
import { CareManagementRatingFields } from "./components/CareManagementRatingFields";
import { CareManagementSectionTitle } from "./components/CareManagementSectionTitle";

import "./components/CareManagementFieldShared.styles.scss";
import "./CareManagementForm.styles.scss";

export function CareManagementForm({
  isEditing,
  isEmbedded = false,
  nameToSign,
  noteState,
  patientId,
  refetchNote,
  setIsEditing,
}) {
  const history = useHistory();
  // used to indicate if everything has loaded
  const disableSubmit = !patientId || !nameToSign;

  /**
   * attach common fields to form
   * session_date is used for indexing even though it
   * does not make sense in this context
   * @param {*} formData
   * @returns
   */
  const prepareNoteForSubmission = (formData) => {
    const clonedFormData = _.cloneDeep(formData);

    clonedFormData["session_date"] = moment().format("MM/DD/YYYY");

    return clonedFormData;
  };

  const submitNote = async (formData) => {
    const urlParams = { patientId };

    if (noteState.note_state === "draft" && formData.note_state === "complete") {
      await providerRequests.put_progress_note({ data: formData, urlParams });

      history.push(`/app/patient/${patientId}/profile/v2?selectedTab=Notes`);
    } else if (noteState.note_state === "draft") {
      await providerRequests.put_progress_note({ data: formData, urlParams });
    } else {
      await providerRequests.post_progress_note({ data: formData, urlParams });

      history.push(`/app/patient/${patientId}/profile/v2?selectedTab=Notes`);
    }
  };

  const preventEnterFormSubmission = (event) => {
    if (event.key === "Enter" || event.code === "Enter") {
      event.preventDefault();
    }
  };

  const renderForm = (formikProps) => {
    // reset the form when it enters view only mode and
    //  is dirty or the note has been updated and the form values need
    //  to be refreshed
    if (!isEditing && (formikProps.dirty || !_.isEqual(noteState, formikProps.values))) {
      formikProps.values = noteState;
      formikProps.resetForm(formikProps);
    }

    const formContent = (
      <>
        {!isEmbedded && (
          <h1
            className="CareManagementForm--title"
            {...getAutomationDataAttr("care-management-assessment-title")}
          >
            Care Management Assessment
          </h1>
        )}

        <div className="CareManagementForm--section">
          <CareManagementSectionTitle>Review A5, A30, DI</CareManagementSectionTitle>

          <CareManagementField
            formikProps={formikProps}
            isViewOnly={!isEditing}
            name="content.review.evaluation"
            subTitle="Review documents (A5, A30, DI, notes, etc.) for salient information. Ask about problems and help needed."
            testId="careManagementAssessmentEvaluationField"
            title="Evaluation"
          />

          <CareManagementField
            formikProps={formikProps}
            isViewOnly={!isEditing}
            name="content.review.understanding_of_diagnosis"
            subTitle="What is the diagnosis and why? How does it affect their life? How have they coped?"
            testId="careManagementAssessmentUnderstandingDiagnosisField"
            title="Understanding of diagnosis"
          />

          <CareManagementField
            formikProps={formikProps}
            isViewOnly={!isEditing}
            name="content.review.difficulties_navigating_health_system"
            subTitle="Focus on attempts to get help, approach used, and outcomes experienced."
            testId="careManagementAssessmentNavigatingHealthSystemField"
            title="Difficulties navigating the health system"
          />

          <CareManagementRadioField
            formikProps={formikProps}
            isViewOnly={!isEditing}
            name="content.review.can_be_assisted_with_life_planning_activities"
            radioOptions={CAN_BE_ASSISTED_WITH_LIFE_PLANNING_ACTIVITIES_OPTIONS}
            subTitle="E.g. living will, DNR, etc."
            testId="careManagementAssessmentLifePlanningActivitiesField"
            title="Can the patient be assisted with the creation of life-planning activities?"
          />
        </div>

        <div className="CareManagementForm--section">
          <CareManagementSectionTitle>Cultural Impact</CareManagementSectionTitle>

          <CareManagementField
            formikProps={formikProps}
            isViewOnly={!isEditing}
            name="content.cultural_impact.family_cultural_views"
            subTitle="Describe views of how one should live, deal with illness & problems, what happens when one dies, and stigmas."
            testId="careManagementAssessmentFamilyCulturalViewsField"
            title="Family or cultural views"
          />

          <CareManagementField
            formikProps={formikProps}
            isViewOnly={!isEditing}
            name="content.cultural_impact.health_care_approach_discouraged"
            subTitle="Describe cultural or religious restrictions to getting help."
            testId="careManagementAssessmentHealthCareDiscouragedField"
            title="Health care approach discouraged"
          />
        </div>

        <div className="CareManagementForm--section">
          <CareManagementSectionTitle>Care Resources</CareManagementSectionTitle>

          <CareManagementRadioField
            formikProps={formikProps}
            isViewOnly={!isEditing}
            name="content.care_resources.help_to_improve_life"
            radioOptions={HELP_TO_IMPROVE_LIFE_OPTIONS}
            testId="careManagementAssessmentHelpToImproveLifeField"
            title="How much help does patient need to improve life?"
          />

          <CareManagementRadioField
            formikProps={formikProps}
            isViewOnly={!isEditing}
            name="content.care_resources.do_others_help_with_life_activities"
            radioOptions={DO_OTHERS_HELP_WITH_LIFE_ACTIVITIES_OPTIONS}
            testId="careManagementAssessmentOthersHelpLifeField"
            title="Do family, friends, or hired help assist with life activities?"
          />

          <CareManagementField
            formikProps={formikProps}
            isViewOnly={!isEditing}
            name="content.care_resources.elaborate_assistance_needed"
            testId="careManagementAssessmentAssistanceNeededField"
            title="Elaborate on assistance needed and caregiver help"
          />

          <CareManagementField
            formikProps={formikProps}
            isViewOnly={!isEditing}
            name="content.care_resources.life_improving_goals"
            subTitle="What changes would make the person happy with their long-term life goals?"
            testId="careManagementAssessmentLifeGoalsField"
            title="Life-improving goals"
          />
        </div>

        <div className="CareManagementForm--section">
          <CareManagementSectionTitle>Conclusion</CareManagementSectionTitle>

          <div className="CareManagementFieldShared--wrapper">
            <div className="CareManagementFieldShared--text_wrapper">
              <h3 className="CareManagementFieldShared--title">Care management focus</h3>

              <h3 className="CareManagementFieldShared--subTitle">
                How severe is the impact of each item? Discuss and reconcile any items that the
                clinician rates differently.
              </h3>
            </div>

            <div className="CareManagementForm--cm_focus_questions">
              <CareManagementRatingFields
                formikProps={formikProps}
                isViewOnly={!isEditing}
                omitLine
                questions={[
                  {
                    name: "content.conclusion.tier_1.risk_of_violence",
                    title: "Risk of violence",
                  },
                  {
                    name: "content.conclusion.tier_1.homelessness_or_housing_insecurity",
                    title: "Homelessness or urgent housing insecurity",
                  },
                  {
                    name: "content.conclusion.tier_1.shelter",
                    title: "Shelter",
                  },
                  {
                    name: "content.conclusion.tier_1.health_emergency",
                    title: "Health Emergency",
                  },
                  {
                    name: "content.conclusion.tier_1.emergency_food_needs",
                    title: "Emergency food needs",
                  },
                  { name: "content.conclusion.tier_1.other", title: "Other" },
                ]}
                subTitle="Immediate action"
                testId="careManagementAssessmentConclusionTier1"
                title="Tier 1"
              />

              <CareManagementRatingFields
                formikProps={formikProps}
                isViewOnly={!isEditing}
                questions={[
                  {
                    name: "content.conclusion.tier_2.urgent_health_conditions",
                    title: "Urgent health condition(s)",
                  },
                  {
                    name: "content.conclusion.tier_2.no_access_medical",
                    title: "No access to medical",
                  },
                  {
                    name: "content.conclusion.tier_2.no_access_food",
                    title: "No access to food",
                  },
                  {
                    name: "content.conclusion.tier_2.recent_admission_or_ed",
                    title: "Recent admission or ED visit with follow up noncompliance",
                  },
                  { name: "content.conclusion.tier_2.other", title: "Other" },
                ]}
                subTitle="Action in the next few days"
                testId="careManagementAssessmentConclusionTier2"
                title="Tier 2"
              />

              <CareManagementRatingFields
                formikProps={formikProps}
                isViewOnly={!isEditing}
                questions={[
                  {
                    name: "content.conclusion.tier_3.chronic_condition",
                    title: "Chronic medical condition",
                  },
                  {
                    name: "content.conclusion.tier_3.home_goods",
                    title: "Home goods / furniture",
                  },
                  { name: "content.conclusion.tier_3.adls", title: "ADLs" },
                  {
                    name: "content.conclusion.tier_3.financial",
                    title: "Financial",
                  },
                  {
                    name: "content.conclusion.tier_3.relationships_social",
                    title: "Relationships / social",
                  },
                  {
                    name: "content.conclusion.tier_3.legal_problems",
                    title: "Legal problems",
                  },
                  {
                    name: "content.conclusion.tier_3.leisure",
                    title: "Leisure",
                  },
                  {
                    name: "content.conclusion.tier_3.educational",
                    title: "Educational",
                  },
                  {
                    name: "content.conclusion.tier_3.employment",
                    title: "Employment",
                  },
                  {
                    name: "content.conclusion.tier_3.limitations",
                    title: "Limitations",
                  },
                  { name: "content.conclusion.tier_3.other", title: "Other" },
                ]}
                subTitle="Action in the next few weeks"
                testId="careManagementAssessmentConclusionTier3"
                title="Tier 3"
              />
            </div>
          </div>

          <CareManagementField
            formikProps={formikProps}
            isViewOnly={!isEditing}
            name="content.conclusion.summary"
            subTitle="What needs to be done to help the patient?"
            testId="careManagementAssessmentSummaryField"
            title="Summary"
          />
        </div>

        {isEditing && (
          <div className="CareManagementForm--section">
            <CareManagementSectionTitle />

            <div className="CareManagementForm--signature_section">
              <ProviderSignatureField
                formikProps={formikProps}
                isViewOnly={false}
                name="signature"
                nameToSign={nameToSign}
                testId="careManagementAssessmentSignature"
              />

              <CredentialField
                formikProps={formikProps}
                isViewOnly={false}
                name="provider_cp_credentials"
              />
            </div>
          </div>
        )}

        {/* only render submit buttons if editing the form  */}
        {isEditing && (
          <div className="CareManagementForm--button_wrapper">
            <Button2
              isDisabled={disableSubmit}
              size="md"
              type="submit"
              {...getAutomationDataAttr("care-management-assessment-submit-button")}
              {...testLocator("careManagementAssessmentSubmitBtn")}
            >
              Sign & Submit
            </Button2>

            <Button2
              isDisabled={disableSubmit}
              onPress={async () => {
                const formData = formikProps.values;
                const clonedFormData = prepareNoteForSubmission(formData);

                clonedFormData["note_state"] = "draft";
                await submitNote(clonedFormData);

                if (noteState?.note_state) {
                  await refetchNote();
                  setIsEditing(false);
                }
              }}
              size="md"
              variant="secondary"
              {...getAutomationDataAttr("care-management-assessment-save-draft-button")}
              {...testLocator("careManagementAssessmentSaveDraftBtn")}
            >
              Save Draft
            </Button2>

            {noteState?.note_state === "draft" && isEditing && (
              <Button2
                className="CareManagementForm--discard_changes_button"
                onPress={() => {
                  // form reset will happen after this state propagates
                  setIsEditing(false);
                }}
                size="md"
                variant="destructive-primary"
                {...getAutomationDataAttr("care-management-assessment-discard-changes-button")}
                {...testLocator("careManagementAssessmentDiscardChangesBtn")}
              >
                Discard Changes
              </Button2>
            )}
          </div>
        )}
      </>
    );

    return (
      <Form key={!isEditing} onKeyDown={preventEnterFormSubmission}>
        {isEmbedded ? (
          <div className="tw:flex tw:flex-col tw:gap-10">{formContent}</div>
        ) : (
          <Card className="CareManagementForm--content_card">{formContent}</Card>
        )}
      </Form>
    );
  };

  return (
    <>
      {noteState && (
        <CustomForm
          initialValues={noteState}
          onSubmit={async (formData) => {
            const clonedFormData = prepareNoteForSubmission(formData);

            clonedFormData["completed_date_time"] = moment().format();
            clonedFormData["note_state"] = "complete";
            await submitNote(clonedFormData);
          }}
          render={renderForm}
          validateOnBlur={true}
          validateOnChange={true}
          validationSchema={CARE_MANAGEMENT_VALIDATION}
        />
      )}
    </>
  );
}
